import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Logo, isApiError } from '@bookora/ui';
import { useLocation, useNavigate } from 'react-router-dom';

import { fetchMe } from '@/api/endpoints/auth';

import { consumeImpersonationHandoff } from './impersonation-handoff';
import { AUTH_EXPIRED_EVENT, identityFromMe, useSessionStore } from './token-store';

/**
 * Boots the session and keeps it honest.
 *
 * Three jobs:
 *
 *  1. **Resolve identity on start.** A persisted token proves nothing — it may
 *     have been revoked from another device, the clinic may have been
 *     suspended, or the user's permissions may have changed. `/auth/me` is the
 *     authoritative answer and it also refills the affordance cache.
 *  2. **React to `auth:expired`.** The axios layer fires it when a refresh
 *     fails; this is where that becomes a navigation, because the interceptor
 *     has no router.
 *  3. **Re-resolve on focus.** A panel left open overnight while the owner
 *     changed a role must not keep showing yesterday's buttons. Cheap, and it
 *     is the only automatic refetch the panel does.
 *  4. **Consume a super-admin impersonation handoff.** The console opens this
 *     panel at `#impersonation=…` with a clinic-scoped token in the fragment.
 *     It is read during the FIRST RENDER, not in an effect: an effect runs
 *     after the boot below has already concluded there is no token and sent the
 *     operator to `/login`, which is precisely the dead link this fixes.
 *     See `impersonation-handoff.ts`.
 *
 * Renders a centred spinner while the first `/auth/me` is in flight, so no
 * screen ever paints with a half-known identity.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Runs exactly once, during the first render, before any effect. `useState`
  // with an initialiser rather than a bare call so a re-render — which React
  // 18's StrictMode guarantees in development — cannot re-read a fragment this
  // has already erased.
  useState(consumeImpersonationHandoff);

  const hydrated = useSessionStore((state) => state.hydrated);
  const accessToken = useSessionStore((state) => state.accessToken);
  const setIdentity = useSessionStore((state) => state.setIdentity);
  const clearSession = useSessionStore((state) => state.clearSession);

  const [resolving, setResolving] = useState(true);
  const resolvedFor = useRef<string | null>(null);

  const resolveIdentity = useCallback(async () => {
    try {
      const me = await fetchMe();
      setIdentity(identityFromMe(me));
    } catch (error) {
      // 401 is already handled by the interceptor (it cleared the session and
      // fired the event). A 403 here means the token is valid but this surface
      // refuses it — a patient token on the staff panel, or a suspended clinic.
      //
      // The reason travels in the URL (`reason=refused&code=…`) rather than as
      // a toast: a toast raised during the navigation to the login page faded
      // while the page changed, and signing in again repeated the loop with no
      // lasting explanation. The login page turns the code into a persistent
      // Persian sentence — with the support contact for a suspended clinic.
      if (isApiError(error) && error.statusCode === 403) {
        clearSession();
        queryClient.clear();
        const code = encodeURIComponent(error.code);
        void navigate(`/login?reason=refused&code=${code}`, { replace: true });
      }
    }
  }, [setIdentity, clearSession, navigate, queryClient]);

  /* -- boot ---------------------------------------------------------------- */

  useEffect(() => {
    if (!hydrated) return;

    if (!accessToken) {
      resolvedFor.current = null;
      setResolving(false);
      return;
    }

    if (resolvedFor.current === accessToken) {
      setResolving(false);
      return;
    }

    let cancelled = false;
    resolvedFor.current = accessToken;
    setResolving(true);

    void resolveIdentity().finally(() => {
      if (!cancelled) setResolving(false);
    });

    return () => {
      cancelled = true;
    };
  }, [hydrated, accessToken, resolveIdentity]);

  /* -- expiry -------------------------------------------------------------- */

  useEffect(() => {
    function handleExpired(): void {
      queryClient.clear();
      // `reason=expired`, so the login page SAYS why it is there. Without it a
      // receptionist mid-task was simply on a login screen, and assumed the
      // panel had crashed and her work was gone.
      const next = `${location.pathname}${location.search}`;
      const params = new URLSearchParams({ reason: 'expired' });
      if (next && next !== '/' && !next.startsWith('/login')) params.set('next', next);
      void navigate(`/login?${params.toString()}`, { replace: true });
    }

    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
  }, [navigate, queryClient, location.pathname, location.search]);

  /* -- refresh identity when the tab comes back ---------------------------- */

  useEffect(() => {
    if (!accessToken) return;

    function handleFocus(): void {
      if (document.visibilityState === 'visible') void resolveIdentity();
    }

    document.addEventListener('visibilitychange', handleFocus);
    return () => document.removeEventListener('visibilitychange', handleFocus);
  }, [accessToken, resolveIdentity]);

  if (!hydrated || resolving) {
    return (
      // A branded splash on the login page's own ground, not a bare spinner
      // on a blank page: this is the first thing every hard refresh shows.
      <div className="bk-login bk-boot" aria-busy="true" aria-live="polite">
        <div className="bk-boot__inner">
          <Logo variant="lockup" size={36} title="بوکورا" />
          <div className="bk-boot__bar" aria-hidden="true" />
        </div>
        <span className="bk-visually-hidden">در حال بررسی ورود به حساب…</span>
      </div>
    );
  }

  return <>{children}</>;
}
